import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { API_URL, getAuthHeaders, getSelectedTeamId, setSelectedTeamId } from '../services/api';

interface TeamLicense {
	type: 'trial' | 'active' | 'expired';
	agent_limit: number;
	seat_limit: number;
	current_agents: number;
	current_seats: number;
	expires_at: number | null;
	days_remaining: number | null;
}

interface Team {
	team_id: string;
	team_name: string;
	role: 'owner' | 'admin' | 'member';
	license: TeamLicense;
}

interface TeamContextType {
	teams: Team[];
	currentTeam: Team | null;
	isLoading: boolean;
	switchTeam: (teamId: string) => Promise<void>;
	refreshTeams: () => Promise<void>;
}

const TeamContext = createContext<TeamContextType | undefined>(undefined);

export function TeamProvider({ children }: { children: React.ReactNode }) {
	const [teams, setTeams] = useState<Team[]>([]);
	const [currentTeam, setCurrentTeam] = useState<Team | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const { firebaseUser, isAuthenticated } = useAuth();

	// Fetch teams list
	const fetchTeams = useCallback(async () => {
		if (!firebaseUser || !isAuthenticated) {
			setTeams([]);
			setCurrentTeam(null);
			// Don't clear localStorage here — Firebase auth is async and this
			// runs before auth is ready on page load. Clearing would wipe the
			// team ID saved by switchTeam before the reload completed.
			setIsLoading(false);
			return;
		}

		try {
			const token = await firebaseUser.getIdToken();
			const response = await fetch(`${API_URL}/api/v1/teams`, {
				headers: getAuthHeaders(token)
			});

			if (!response.ok) {
				throw new Error('Failed to fetch teams');
			}

			const data = await response.json();
			const fetchedTeams: Team[] = data.teams || [];
			setTeams(fetchedTeams);

			if (fetchedTeams.length > 0) {
				// Restore previously selected team from localStorage
				const savedTeamId = getSelectedTeamId();
				const savedTeam = savedTeamId
					? fetchedTeams.find(t => t.team_id === savedTeamId)
					: null;

				if (savedTeam) {
					setCurrentTeam(savedTeam);
				} else {
					// Prefer a non-trial team when no team is saved, fall back to first team
					const nonTrialTeam = fetchedTeams.find(t => t.license?.type !== 'trial');
					const defaultTeam = nonTrialTeam || fetchedTeams[0];
					setCurrentTeam(defaultTeam);
					setSelectedTeamId(defaultTeam.team_id);
				}
			} else {
				setCurrentTeam(null);
				setSelectedTeamId(null);
			}
		} catch (error) {
			console.error('Failed to fetch teams:', error);
			setTeams([]);
			setCurrentTeam(null);
			// Don't clear localStorage on network errors — preserve team selection
		} finally {
			setIsLoading(false);
		}
	}, [firebaseUser, isAuthenticated]);

	// Switch to a different team
	const switchTeam = async (teamId: string) => {
		const team = teams.find(t => t.team_id === teamId);
		if (!team) {
			throw new Error('Team not found');
		}

		// Persist selected team to localStorage, then reload to flush all stale data
		setSelectedTeamId(teamId);
		window.location.reload();
	};

	// Refresh teams list
	const refreshTeams = async () => {
		setIsLoading(true);
		await fetchTeams();
	};

	// Fetch teams on mount and when auth changes
	useEffect(() => {
		fetchTeams();
	}, [fetchTeams]);

	const value: TeamContextType = {
		teams,
		currentTeam,
		isLoading,
		switchTeam,
		refreshTeams,
	};

	return <TeamContext.Provider value={value}>{children}</TeamContext.Provider>;
}

export function useTeam() {
	const context = useContext(TeamContext);
	if (context === undefined) {
		throw new Error('useTeam must be used within a TeamProvider');
	}
	return context;
}
